/**
 * Fare Calculator Utility
 * Calculates delivery fare based on distance and vehicle type
 */

import { vehicleTypes, pricingConfig } from "../config/companyConfig";

/**
 * Calculate distance between two coordinates (in kilometers)
 * Using Haversine formula
 * @param {number} lat1 - Pickup latitude
 * @param {number} lng1 - Pickup longitude
 * @param {number} lat2 - Drop latitude
 * @param {number} lng2 - Drop longitude
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate delivery fare
 * Formula: baseFare + (distance * costPerKm) + platformFee + GST
 * @param {number} distance - Distance in kilometers
 * @param {string} vehicleType - Vehicle type ID (BIKE, SCOOTER, CAR, TRUCK)
 * @returns {object} Fare breakdown
 */
export const calculateFare = (distance, vehicleType) => {
  // Validate inputs
  if (!distance || distance <= 0) {
    return {
      baseFare: 0,
      distanceCharge: 0,
      platformFee: 0,
      gst: 0,
      totalFare: 0,
      error: "Invalid distance",
    };
  }

  if (!vehicleType || !vehicleTypes[vehicleType]) {
    return {
      baseFare: 0,
      distanceCharge: 0,
      platformFee: 0,
      gst: 0,
      totalFare: 0,
      error: "Invalid vehicle type",
    };
  }

  const vehicle = vehicleTypes[vehicleType];

  // Check if distance exceeds vehicle's max distance
  if (distance > vehicle.maxDistance) {
    return {
      baseFare: 0,
      distanceCharge: 0,
      platformFee: 0,
      gst: 0,
      totalFare: 0,
      error: `This vehicle can only deliver up to ${vehicle.maxDistance} km`,
    };
  }

  // Calculate fare components
  const baseFare = vehicle.baseFare;
  const distanceCharge = distance * vehicle.costPerKm;
  const platformFee = pricingConfig.platformFee;

  // Apply surge pricing if active
  let fareBeforeTax = baseFare + distanceCharge + platformFee;
  if (pricingConfig.surgeChargeActive) {
    fareBeforeTax *= pricingConfig.surgeMultiplier;
  }

  // Apply minimum fare if needed
  const fareBeforeTaxFinal = Math.max(fareBeforeTax, pricingConfig.minFare);

  // Calculate GST
  const gst = Math.round(fareBeforeTaxFinal * pricingConfig.gstRate * 100) / 100;
  const totalFare = Math.round((fareBeforeTaxFinal + gst) * 100) / 100;

  return {
    baseFare: Math.round(baseFare * 100) / 100,
    distanceCharge: Math.round(distanceCharge * 100) / 100,
    platformFee: platformFee,
    fareBeforeTax: Math.round(fareBeforeTaxFinal * 100) / 100,
    gst: gst,
    totalFare: totalFare,
    surge: pricingConfig.surgeChargeActive ? pricingConfig.surgeMultiplier : 1,
    error: null,
  };
};

/**
 * Get time estimate for delivery
 * @param {number} distance - Distance in kilometers
 * @param {string} vehicleType - Vehicle type ID
 * @returns {string} Time estimate text
 */
export const getTimeEstimate = (distance, vehicleType) => {
  if (!vehicleType || !vehicleTypes[vehicleType]) return "N/A";

  const vehicle = vehicleTypes[vehicleType];

  // Rough estimate: divide distance by avg speed, add 10 mins for pickup/drop
  const speedStr = vehicle.avgSpeed.split(" ")[0];
  const avgSpeed = parseInt(speedStr);

  if (isNaN(avgSpeed)) return vehicle.timeEstimate;

  const travelTime = Math.ceil(distance / avgSpeed);
  const totalTime = travelTime + 10; // Add 10 mins for pickup and drop

  if (totalTime < 30) {
    return "30-45 mins";
  } else if (totalTime < 60) {
    return "45 mins - 1 hour";
  } else if (totalTime < 120) {
    return "1-2 hours";
  } else {
    return "2+ hours";
  }
};

const fareCalculator = {
  calculateDistance,
  calculateFare,
  getTimeEstimate,
};

export default fareCalculator;
